// src/Components/Hooks/useQrImageJobProgress.ts
// Polls QR image export job progress while a job is active, then stops on terminal status.
// Xem usePrintPackageJobProgress.ts — cùng pattern, áp dụng cho luồng xuất ảnh QR song song.

import { useEffect, useRef } from 'react';
import { getQrImageExportJob } from 'helpers/fakebackend_helper';
import { toJobStatus } from 'pages/SinhBoSoQr/types';

const POLLING_INTERVAL_MS = 5000;

export interface QrImageJobProgressMessage {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | string;
  processedRows: number;
  totalRows: number;
  progressPercent: number;
  fileName?: string | null;
  errorMessage?: string | null;
}

const normalizeProgressMessage = (message: any): QrImageJobProgressMessage => ({
  jobId: message.jobId ?? message.JobId,
  status: toJobStatus(message.status ?? message.Status),
  processedRows: message.processedRows ?? message.ProcessedRows ?? 0,
  totalRows: message.totalRows ?? message.TotalRows ?? 0,
  progressPercent: message.progressPercent ?? message.ProgressPercent ?? 0,
  fileName: message.fileName ?? message.FileName ?? null,
  errorMessage: message.errorMessage ?? message.ErrorMessage ?? null,
});

export const useQrImageJobProgress = (
  jobId: string | null,
  onProgress: (message: QrImageJobProgressMessage) => void,
) => {
  const onProgressRef = useRef(onProgress);
  const pollingTimerRef = useRef<number | null>(null);
  onProgressRef.current = onProgress;

  useEffect(() => {
    if (!jobId) return;

    let disposed = false;
    let inFlight = false;

    const clearPolling = () => {
      if (pollingTimerRef.current !== null) {
        window.clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };

    const pollProgress = async () => {
      if (disposed || inFlight) return;
      inFlight = true;
      try {
        const response = await getQrImageExportJob(jobId);
        if (disposed) return;

        const payload = (response as any)?.data || response;
        const message = normalizeProgressMessage(payload);
        if (message.jobId !== jobId) return;

        onProgressRef.current(message);
        const status = String(message.status || '').toLowerCase();
        if (status === 'completed' || status === 'failed' || status === 'stalled' || status === 'cancelled') {
          clearPolling();
        }
      } catch {
        // Keep polling; transient API failures should not kill progress tracking.
      } finally {
        inFlight = false;
      }
    };

    pollProgress();
    pollingTimerRef.current = window.setInterval(pollProgress, POLLING_INTERVAL_MS);

    return () => {
      disposed = true;
      clearPolling();
    };
  }, [jobId]);
};
